<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from 'vue'
import router from '@/router'
import { getArticleList, getLegacyArticleList } from '@/api/http'
import { useRoute } from 'vue-router'
import { getEditorData, getHeadsetType, getSupportAreaCode, getSupportPath, getMarkLogs, isUrlFixDomain } from '@/utils/common'
import { normalizeVideoSubtitles } from '@/utils/subtitles'
import { useInternationalStore } from '@/store/international'
import { useI18n } from 'vue-i18n'
import VideoPlayer from '../../../../../components/VideoPlayer.vue'
import { getLegacyArticleSectionType, isLegacySeeMoreLink } from '@/utils/supportLegacy'

const { t } = useI18n()
const internationalStore = useInternationalStore()

const route = useRoute()
const videoPlayerRef = ref<InstanceType<typeof VideoPlayer> | null>(null)
/*
 * 判断当前 seeMore 是否是旧链接
 * 旧 seeMore 链接保留旧移动端列表样式，但数据源切到旧接口
 */
const legacySeeMore = computed(() => isLegacySeeMoreLink(route.query))

onMounted(() => {
  getEditorList({ 
    headset_model: legacySeeMore.value ? route.query.headsetType : getHeadsetType(route.params.product as string),
    area_code: getSupportAreaCode(internationalStore.location.abb, route.path)
  })
})

const skeleton = ref(false)
const tabs = computed(() => [
  { 
    title: t('ContentType.guide'), 
    type: 'service_manual', 
    list: []
  },
  { 
    title: t('ContentType.troubleshooting'), 
    type: 'troubleshooting', 
    list: []
  },
  { 
    title: t('ContentType.videos'), 
    type: 'video_tutorial', 
    list: []
  }
])

const activeTab = ref('')
const activeType = ref('')
const clickType = (value: string) => {
  if (activeType.value === value) activeType.value = ''
  else activeType.value = value
}

const sessionActiveType = computed(() => sessionStorage.getItem('activeType') || '')
const menuList: any = ref([])
/*
 * 获取菜单列表
 * @param param - 服务支持接口请求参数
 */
const getEditorList = (param) => {
	console.log('获取菜单列表: ', param);
	
  skeleton.value = true
  /*
   * 选择服务支持接口
   * 旧链接按原 headsetType 请求旧接口，新链接继续按产品路由请求 v2
   */
  const requestArticleList = legacySeeMore.value ? getLegacyArticleList : getArticleList
  requestArticleList(param)
    .then((res: any) => {
      res.support_list.forEach((item) => {
        tabs.value.forEach((e) => {
          if (item.type === e.type) e.list = classify(item)
        })
      })
			let index = tabs.value.findIndex((item) => item.list.length > 0)
			
      if (route?.query?.activeType) handleTab(tabs.value[tabs.value.findIndex((tab) => tab.type === route?.query?.activeType)])
      else if (legacySeeMore.value) handleTab(tabs.value[tabs.value.findIndex((tab) => tab.type === getLegacyArticleSectionType(route.query.articleType))])
      else if (sessionActiveType.value) handleTab(tabs.value[tabs.value.findIndex((tab) => tab.type === sessionActiveType.value)])
      else handleTab(tabs.value[index])
      skeleton.value = false
    })
    .catch(() => {
      menuList.value = []
      skeleton.value = false
    })
}

// 数据分类
const classify = (obj: { subtype_list: any[]; info_list: any[]; type: string }) => {
  // 视频类型获取视频地址
  if (obj.type === 'video_tutorial') {
    obj.info_list.forEach((item: any) => {
      item.subtitles = normalizeVideoSubtitles(item)
      // 判断视频url是否以固定域名开始，是则需要执行地址解析，否则不需要
      if (isUrlFixDomain(item.url)) {
        getEditorData(item.url).then((res: any) => item.url = res);
      }
    })
  }
  // 数据分类
  let arr: any = []
  obj?.subtype_list?.forEach((item: any[]) => {
    let info_list = obj.info_list.filter((e) => e.name === item)
    arr = [...arr, { type: item, list: info_list }]
  })
  return arr
}

/*
 * 切换当前文章类型
 * @param item - 当前 tab 数据
 */
const handleTab = (item) => {
  if (!item) return
  sessionStorage.setItem('activeType', item.type)
  activeTab.value = item.type
  menuList.value = item.list
}

/*
 * 跳转文章详情
 * @param item - 当前文章数据
 */
const toDetail = (item: any) => {
  // 发现页埋点配置
  if (item.id) getMarkLogs(item.id)
  if (legacySeeMore.value) {
    router.push({
      path: `/support/detail/${item.id}/${activeTab.value}`,
      query: {
        url: item.url,
        title: item.title
      }
    })
    return
  }
  router.push({
    path: `/support/detail/${item.id}/${activeTab.value}`,
    query: {
      product: route.params.product as string,
      activeType: activeTab.value,
      categoryName: item.name || ''
    }
  })
}

const handleVideo = (item: any) => {
  videoPlayerRef.value?.playVideo(item)
}

onUnmounted(() => {
  sessionStorage.removeItem('activeType')
})
</script>

<template>
  <main class="nanoPlus">
    <ul class="breadcrumb">
      <li @click="router.push('/support/index')">{{ $t('Support.product.home') }}</li>
      &nbsp;&nbsp;>&nbsp;&nbsp;
      <li @click="router.back()">viaim {{ getSupportPath(route.params.product as string) }} {{ $t('Support.product.support') }}</li>
      &nbsp;&nbsp;>&nbsp;&nbsp;
      <li>{{ $t('Support.product.inst') }}</li>
    </ul>
    <div class="main">
      <ul class="GuideTroubleshooting">
        <li v-for="item in tabs" :key="item.title" :class="{ selected: item.type === activeTab }" @click="handleTab(item)" v-show="(item.list.length > 0 && !skeleton) || skeleton">{{ item.title }}</li>
      </ul>
      <el-skeleton animated class="select" :loading="skeleton">
        <template #template>
          <el-skeleton-item class="skeleton-tab-title" variant="caption" />
          <div class="Troubleshooting">
            <div class="skeleton-content" v-for="item2 in 6" :key="item2">
              <el-skeleton-item variant="text" />
              <el-skeleton-item variant="text" />
            </div>
          </div>
          <el-skeleton-item class="skeleton-tab-title" variant="caption" v-for="item2 in 1" :key="item2" />
        </template>
        <template #default>
          <div class="select" v-for="item in menuList" :key="item.id">
            <div class="tab-title" @click="clickType(item.type)">
              {{ item.type }}
              <svg class="iconpark-icon" :class="{ selected: item.type === activeType }">
                <use href="#Property1Variant4"></use>
              </svg>
            </div>
            <!-- Guide & Troubleshooting -->
            <ul class="Troubleshooting" :class="{ 'Troubleshooting-no-video': activeTab !== 'video_tutorial' }" v-show="item.type === activeType">
              <template v-if="activeTab !== 'video_tutorial'">
                <li class="li" v-for="item2 in item.list" :key="item2.id" @click="toDetail(item2)">
                  <div class="column">{{ item2.title }}</div>
                  <div class="icon">
                    <svg class="iconpark-icon">
                      <use href="#Property1Variant4"></use>
                    </svg>
                  </div>
                </li>
              </template>
              <template v-else>
                <div class="video-card" v-for="item2 in item.list" :key="item2.id">
                  <!-- <video class="video-content" :poster="item2.pic" :src="item2.url"></video> -->
                  <div class="skeleton-video" @click="handleVideo(item2)">
                    <img class="video-content" :src="item2.pic" />
                    <svg class="iconpark-icon video-icon">
                      <use href="#Property1tiaozhuanshibofanganniu"></use>
                    </svg>
                  </div>
                  <span class="video-title">{{ item2.title }}</span>
                </div>
              </template>
            </ul>
          </div>
        </template>
      </el-skeleton>
    </div>
  </main>
  <VideoPlayer ref="videoPlayerRef" />
</template>

<style lang="scss" scoped>
.nanoPlus {
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 16rem);
  display: flex;
  align-items: center;
  flex-direction: column;
  background: #f6f6f6;

  .breadcrumb {
    padding: 0 1rem;
    display: flex;
    align-items: center;
    width: 100%;
    height: 5.6rem;
    color: #575757;
    background-color: #f2f2f2;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 400;
    line-height: 2rem;
  }

  .main {
    width: 100%;
    height: 100%;
    padding: 0 1.6rem 2.4rem 1.6rem;
    background: #f6f6f6;

    .GuideTroubleshooting {
      display: flex;
      width: 100%;
      height: 4.6rem;
      align-items: center;
      color: #989898;

      li {
        margin: 0 1.2rem;
        font-size: 1.6rem;
        font-style: normal;
        font-weight: 600;
        height: 4.6rem;
        display: flex;
        align-items: center;
        position: relative;
      }

      .selected {
        color: #202020;
      }

      .selected::after {
        content: '';
        width: calc(100% + 0.4rem);
        height: 0.2rem;
        background-color: #000;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
      }
    }

    .select {
      margin-top: 1.6rem;
      width: 100%;
      gap: 1rem;
      color: #000;
      font-size: 1.6rem;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      position: relative;

      .skeleton-tab-title {
        height: 6.2rem;
        margin-top: 1.6rem;
      }

      .tab-title {
        height: 6.2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 2.4rem;
        border-radius: 0.7rem;
        border: 0.5px solid #d3d3d3;

        .iconpark-icon {
          transition: all 0.2s linear;
        }

        .selected {
          transform: rotate(90deg);
        }
      }

      .slide-down-enter-active {
        transition: all 0.3s;
      }

      .slide-down-leave-active {
        transition: all 0.3s;
      }

      .slide-down-enter,
      .slide-down-leave-to {
        max-height: 0;
        opacity: 0;
      }

      .Troubleshooting-no-video {
        padding: 0 2.4rem;
        background-color: #fff;
        border-radius: 1rem;
      }

      .Troubleshooting {
        padding-top: 2rem;
        padding-bottom: 2rem;
        margin-top: 0.8rem;
        width: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;

        .skeleton-content {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.7rem 0;

          :nth-child(1) {
            width: 75%;
            height: 2rem;
          }

          :nth-child(2) {
            width: 15%;
            height: 2rem;
          }
        }

        .video-card {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          .skeleton-video {
            width: 100%;
            aspect-ratio: 16/9;
            margin-bottom: 1.4rem;
            position: relative;

            .video-content {
              width: 100%;
              aspect-ratio: 16/9;
              border-radius: 0.3rem;
            }

            .video-icon {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 6rem;
              height: 6rem;
            }
          }

          .video-title {
            margin-top: 1rem;
            margin-bottom: 3.4rem;
            color: #000;
            font-size: 1.4rem;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          }
        }

        .li {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;

          .column {
            padding: 1.7rem 0;
            color: #000;
            font-size: 1.4rem;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
          }

          .icon {
            margin-left: 2.6rem;
            width: 2.4rem;
            height: 2.8rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  }
}
</style>
